import { useSyncExternalStore, useEffect } from "react";
import type { EventLog, Log } from "ethers";
import { useContracts } from "@/hooks/useContracts";
import { getContracts, getProvider, DEPLOY_BLOCK } from "@/lib/contracts";

export interface UserCat {
  tokenId: number;
}

/**
 * 持有列表采用「共享 store + 手动刷新」而非定时轮询。
 *
 * 三个组件（MyCatsStrip / OperationsDashboard / FusePanel）都要用这份数据，
 * 各自轮询会造成三倍 RPC 消耗，且各自持有独立 state 时
 * 一个组件的 refresh 不会同步到另一个组件。
 *
 * 因此改为模块级 store：全局只保存一份数据，任何一次刷新都会通知所有订阅者。
 * 数据只在「首次挂载 / 切换账户 / 交易完成」时拉取，不做定时轮询。
 */

interface Snapshot {
  cats: UserCat[];
  isLoading: boolean;
}

const EMPTY: Snapshot = { cats: [], isLoading: false };
let snapshot: Snapshot = EMPTY;
const listeners = new Set<() => void>();

function emit(next: Snapshot) {
  snapshot = next;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** useSyncExternalStore 要求同一状态返回同一引用，这里靠整体替换 snapshot 保证 */
function getSnapshot() {
  return snapshot;
}

/** 当前账户；由 useUserCats 在账户变化时写入 */
let currentAccount: string | null = null;

/**
 * 按账户缓存持有集合与已扫描到的区块高度。
 *
 * 扫 Transfer 日志重建持有列表：合约的 mint / transfer / burn 三条路径都会 emit Transfer，
 * 且 tokenId 为 indexed，因此每次刷新只需 2 个 getLogs，与铸造量无关。
 * 二次刷新只扫新增区块。
 */
const ownershipCache = new Map<string, { tokens: Set<number>; lastBlock: number }>();

/** 同一时刻只允许一次扫描，多个组件同时挂载时合并为一次请求 */
let inflight: Promise<void> | null = null;

/** 当前账户是否已成功加载过。不能用 cats.length 判断，否则「没有猫」会被反复重扫 */
let hasLoaded = false;

/** 清空持有缓存，下次刷新会从部署块重新全量扫描 */
export function resetUserCatsCache(account?: string) {
  if (account) ownershipCache.delete(account.toLowerCase());
  else ownershipCache.clear();
}

async function scan(account: string) {
  const provider = getProvider();
  const { nft } = getContracts(provider);
  const key = account.toLowerCase();

  let entry = ownershipCache.get(key);
  const isFirstScan = !entry;
  if (!entry) {
    entry = { tokens: new Set<number>(), lastBlock: DEPLOY_BLOCK - 1 };
    ownershipCache.set(key, entry);
  }

  const latestBlock = await provider.getBlockNumber();
  const fromBlock = entry.lastBlock + 1;

  const replay = (events: (EventLog | Log)[]) => {
    events
      .filter((ev): ev is EventLog => "args" in ev && (ev as EventLog).args !== undefined)
      .sort((a: Log, b: Log) => a.blockNumber - b.blockNumber || a.index - b.index)
      .forEach((ev) => {
        const to = String(ev.args[1]).toLowerCase();
        const tokenId = Number(ev.args[2]);
        if (to === key) entry!.tokens.add(tokenId);
        else entry!.tokens.delete(tokenId);
      });
  };

  if (fromBlock <= latestBlock) {
    // 转入（to = account）与转出（from = account），两个请求会被合并进同一 batch
    const [incoming, outgoing] = await Promise.all([
      nft.queryFilter(nft.filters.Transfer(null, account), fromBlock, latestBlock),
      nft.queryFilter(nft.filters.Transfer(account, null), fromBlock, latestBlock),
    ]);
    replay([...incoming, ...outgoing]);
    entry.lastBlock = latestBlock;

    // 安全网：首次扫描后用 balanceOf 校验。
    // 若 VITE_DEPLOY_BLOCK 配置得比真实部署块晚（例如换了合约却忘了更新），
    // 早期的 Transfer 会被漏掉，持有列表静默出错。这里发现不一致就从创世块重扫。
    if (isFirstScan && DEPLOY_BLOCK > 0) {
      const onChainCount = Number(await nft.balanceOf(account));
      if (onChainCount !== entry.tokens.size) {
        console.warn(
          `[useUserCats] 持有数与链上不一致（扫描 ${entry.tokens.size} / 链上 ${onChainCount}），` +
            `VITE_DEPLOY_BLOCK=${DEPLOY_BLOCK} 可能配置有误，回退为全量扫描`
        );
        entry.tokens.clear();
        const [allIn, allOut] = await Promise.all([
          nft.queryFilter(nft.filters.Transfer(null, account), 0, latestBlock),
          nft.queryFilter(nft.filters.Transfer(account, null), 0, latestBlock),
        ]);
        replay([...allIn, ...allOut]);
      }
    }
  }

  hasLoaded = true;
  emit({
    cats: [...entry.tokens].sort((a, b) => a - b).map((tokenId) => ({ tokenId })),
    isLoading: false,
  });
}

/**
 * 主动刷新持有列表，所有订阅组件会同步更新。
 *
 * 需要在每次改变持有关系的交易成功后调用（mint / fuse / transfer）。
 */
export async function refreshUserCats(): Promise<void> {
  const account = currentAccount;
  if (!account) {
    hasLoaded = false;
    emit(EMPTY);
    return;
  }
  // 合并并发调用，避免多个组件同时触发时重复扫描
  if (inflight) return inflight;

  emit({ cats: snapshot.cats, isLoading: true });
  inflight = scan(account)
    .catch((err) => {
      console.error("[useUserCats] 读取持有列表失败", err);
      // 扫描失败时保留上一次结果，不要把已有的猫清空
      emit({ cats: snapshot.cats, isLoading: false });
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

export function useUserCats() {
  const { account, connected } = useContracts();
  const state = useSyncExternalStore(subscribe, getSnapshot);

  // 仅在首次挂载与账户/连接状态变化时拉取一次，不做定时轮询
  useEffect(() => {
    const next = connected && account ? account : null;
    if (next === currentAccount) {
      // 同一账户下另一个组件稍后挂载时，复用已加载的数据，不重复请求
      if (next && !hasLoaded && !inflight) refreshUserCats();
      return;
    }
    currentAccount = next;
    hasLoaded = false;
    refreshUserCats();
  }, [account, connected]);

  return { cats: state.cats, isLoading: state.isLoading, refresh: refreshUserCats };
}
